'use client';

import React from 'react';

import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { useProductCompare } from '../../../../hooks/use-product-compare';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';

interface Props {
   product: NormalizedProduct;
   locale: string;
   baseRoute: string;
   hasSharedProducts: boolean;
}

export const ProductCompareCardHeader = ({ product, locale, baseRoute, hasSharedProducts }: Props) => {
   const translate = useTranslations();
   const { removeProduct } = useProductCompare();
   const { desktop } = useBreakpoint();

   const handleRemoveClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      removeProduct(product.id);
   };

   return (
      <TableHeader paddingX="sm" width={desktop ? undefined : '300px'} height="1px" raw>
         {product.brand && !product.id.includes('empty-') && (
            <Box direction="column" height="100%" width="100%">
               <DynamicPageLink
                  id={`dynamic-link__product-compare-avatar__${product.slug}`}
                  baseRoute={{ url: baseRoute, noFollow: product.seoMetadata?.noFollow }}
                  slug={product.slug}
                  locale={locale}
                  underline="none"
                  height="100%"
               >
                  <Box
                     gap="2xs"
                     borderRadius="largest"
                     backgroundColor="primary"
                     direction="column"
                     position="relative"
                     spacing="md"
                     width="100%"
                     height="100%"
                     justify="between"
                  >
                     <Box justify="between" items="start" position="relative" zIndex="2">
                        <Typography whiteSpace="normal" variant="body-highlight" color="on-dark-strong">
                           {product.name}
                        </Typography>
                        {!hasSharedProducts && (
                           <button type="button" aria-label="Remove product" onClick={handleRemoveClick}>
                              <Icon icon="close" color="inverse-strong" />
                           </button>
                        )}
                     </Box>
                     <Box justify="between" position="relative" zIndex="2">
                        <Typography component="span" variant="small" color="inverse-subtle">
                           {product.ulProductId ? '' : translate('page-consumer.product-compare.limited-information-description')}
                        </Typography>
                        <IconButton backdrop variant="on-light" icon="chevron-right" />
                     </Box>
                     <Box
                        position="absolute"
                        bottom="0"
                        left="0"
                        zIndex="1"
                        height="100%"
                        width="100%"
                        borderRadius="largest"
                        backgroundColor="gradient-fade-out-black"
                     />
                  </Box>
               </DynamicPageLink>
            </Box>
         )}
      </TableHeader>
   );
};
